import { storage } from 'utils/typedStorage';
import React, { useEffect, useState, type ComponentType } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import axios from 'api/axiosConfig';
import { getCandidates } from 'api/generated/api/candidates';
import { getApiErrorStatus } from 'utils/apiErrorResponse';
import { useSessionVerification } from 'hooks/useSessionVerification';
import { defaultFromPageForShareTranscriptLink } from 'utils/shareTranscriptLink';
import { toSameOriginPath } from 'utils/sameOriginPath';
import { useNeedsSupportSelection } from 'hooks/useNeedsSupportSelection';
import { paths, useAppNavigate } from 'url-registry';
import { ROUTE_AFTER_REDIRECT_KEY } from 'utils/bullhornEmbed';
import { lazyWithReload } from 'utils/lazyWithReload';
import { isSharedPagePath } from 'utils/sharedPagePath';
import { startSessionReplayWhenIdle } from 'utils/sessionReplay';
import {
  installShareSessionInterceptors,
  startShareSession,
  stopShareSession,
  useShareSessionStore,
} from 'share-links/shareSession';

// Only support users pick a role before entering, so the picker and its entity
// finder load on demand. It renders in the page slot, under Layout's Suspense.
const SelectSupportRole = lazyWithReload(
  () => import('pages/login/SelectSupportRole')
);

// The shared report's notices load with it rather than in the shell, which carries no
// recruiter strings. (The page itself renders its open-in-Tenzo banner.)
const SharedReportStatusView = lazyWithReload(
  () => import('share-links/SharedReportStatusView')
);

const ShareAccessLoadError = lazyWithReload(() =>
  import('components/ChunkErrorFallback').then(({ ChunkErrorFallback }) => ({
    default: ChunkErrorFallback,
  }))
);

type PageComponent = ComponentType<any>; // eslint-disable-line @typescript-eslint/no-explicit-any -- route pages have distinct props

export const ProtectedRoute = ({
  component: Component,
}: {
  component: PageComponent;
}) => {
  const { isLoading, isProvisional, isAuthenticated, isAccountReady } =
    useSessionVerification();
  const location = useLocation();
  const navigate = useAppNavigate();
  const needsSupportSelection = useNeedsSupportSelection();
  const shouldRedirect = !isAuthenticated && (!isLoading || isProvisional);
  const savedRoute = location.pathname + location.search;

  useEffect(() => {
    if (shouldRedirect) {
      storage.setDynamic(ROUTE_AFTER_REDIRECT_KEY, savedRoute);
      navigate(paths.LOGIN, undefined, undefined, { replace: true });
    }
  }, [shouldRedirect, savedRoute, navigate]);

  // A provisional session (the cached last verified one) renders the page while
  // verification is in flight; the verified response reconciles it.
  if (isLoading && !isProvisional) {
    return <></>;
  }

  if (!isAuthenticated) return null;

  if (needsSupportSelection) {
    return <SelectSupportRole />;
  }

  if (!isAccountReady) return null;

  return <Component />;
};

export const LoginRoute = ({
  component: Component,
}: {
  component: PageComponent;
}) => {
  const { isLoading, isAuthenticated } = useSessionVerification();
  const needsSupportSelection = useNeedsSupportSelection();

  if (isLoading) {
    return <></>;
  }

  if (needsSupportSelection) {
    return <SelectSupportRole />;
  }

  return !isAuthenticated ? (
    <Component />
  ) : (
    <Navigate replace to={paths.HOME} />
  );
};

export const ShareLinkRoute = ({
  component: Component,
}: {
  component: PageComponent;
}) => {
  const [searchParams] = useSearchParams();
  const candidateId = searchParams.get('candidateId') || '';
  const campaignId = searchParams.get('campaignId') || '';
  const fromPage = searchParams.get('fromPage') || '';
  const { isLoading: isAuthLoading, isAccountReady } = useSessionVerification();

  const [isLoading, setIsLoading] = useState(true);
  const [accessLoadFailed, setAccessLoadFailed] = useState(false);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    setAccessLoadFailed(false);
    setIsLoading(true);
    if (!isAccountReady) {
      setIsLoading(false);
      return;
    }

    // The staff context checks the session's job permission before in-app navigation.
    let cancelled = false;
    const checkCampaignOrg = async () => {
      try {
        if (campaignId && candidateId) {
          const data =
            await getCandidates().candidatesGetTranscriptSharingContext(
              candidateId,
              campaignId
            );
          if (cancelled) return;
          if (data.can_view_in_app) {
            window.location.href =
              (fromPage ? toSameOriginPath(fromPage) : null) ||
              defaultFromPageForShareTranscriptLink(
                window.location.origin,
                candidateId,
                campaignId
              );
            return;
          }
        }
      } catch (error) {
        if (cancelled) return;
        const status = getApiErrorStatus(error);
        if (status !== 401 && status !== 403 && status !== 404) {
          console.error('Error checking campaign org:', error);
          setAccessLoadFailed(true);
        }
      }
      if (!cancelled) setIsLoading(false);
    };

    void checkCampaignOrg();
    return () => {
      cancelled = true;
    };
  }, [candidateId, campaignId, fromPage, isAuthLoading, isAccountReady]);

  if (isLoading) return <></>;
  if (accessLoadFailed) {
    return <ShareAccessLoadError onReload={() => window.location.reload()} />;
  }
  return <Component />;
};

export const SubmittalReportsRoute = ({
  component: Component,
}: {
  component: PageComponent;
}) => {
  const { isLoading: isAuthLoading, isAuthenticated } =
    useSessionVerification();

  if (isAuthLoading) {
    return <></>;
  }

  // The report's own address is staff-only; the page asks a signed-out visitor to sign in
  // rather than redirecting, since a client holding an old link has no Tenzo account.
  return <Component isLoggedIn={isAuthenticated} />;
};

/**
 * The share-link report page. It exchanges the secret in the URL fragment for a share session,
 * and while mounted every request carries the session's capability. The page renders once the
 * exchange succeeds; an unusable link renders why instead.
 */
export const SharedSubmittalReportRoute = ({
  component: Component,
}: {
  component: PageComponent;
}) => {
  const { hash } = useLocation();
  // Waited for so the page's open-in-Tenzo banner, for a signed-in member of the report's
  // org, is there from the first paint.
  const { isLoading: isAuthLoading } = useSessionVerification();
  const status = useShareSessionStore((state) => state.status);
  const session = useShareSessionStore((state) => state.session);

  useEffect(() => {
    // The viewer is outside the org and the URL carries a secret: no session replay here.
    datadogRum.stopSessionReplayRecording();
    const eject = installShareSessionInterceptors(axios);
    return () => {
      eject();
      stopShareSession();
      // Leaving for an in-app page (Open in Tenzo): record it, as a page load there would.
      if (!isSharedPagePath(window.location.pathname)) {
        startSessionReplayWhenIdle();
      }
    };
  }, []);

  // The fragment is left in the address bar so a reload opens the report again.
  useEffect(() => {
    void startShareSession(hash);
  }, [hash]);

  if (status === 'loading' || isAuthLoading) {
    return <></>;
  }

  if (status !== 'ready' || !session) {
    return (
      <SharedReportStatusView
        status={status === 'ready' ? 'invalid' : status}
        onRetry={() => {
          void startShareSession(window.location.hash, { force: true });
        }}
      />
    );
  }

  return <Component />;
};

export const RestrictedAccessRoute = ({
  component: Component,
}: {
  component: PageComponent;
}) => {
  const { isLoading, isAuthenticated } = useSessionVerification();

  if (isLoading) {
    return <></>;
  }

  return <Component isLoggedIn={isAuthenticated} />;
};

export const PreserveSearchRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};
